import type { PageContent } from "../types";

export const p_voice_search_and_conversational_queries_in_2023: PageContent = {
  slug: "voice-search-and-conversational-queries-in-2023",
  title: `Voice Search and Conversational Queries in 2023 | Phil Carr SEO Consultant`,
  description: `Although voice search did not overtake traditional search in 2023, it continued to grow steadily as smart speakers and voice assistants became more integrated into…`,
  date: "",
  lastUpdated: "2026-05-20",
  body: `# Voice Search and Conversational Queries in 2023

![Voice Search and Conversational Queries in 2023 featured image](/images/blog/generated/voice-search-and-conversational-queries-in-2023.jpg)

**Published:** June 15, 2023

## The Growing Role of Voice

Although voice search did not overtake traditional search in 2023, it continued to grow steadily as smart speakers and voice assistants became more integrated into daily life. People now use their voices to ask quick questions, perform on‑the‑go tasks, and search for local businesses.

Voice queries tend to be longer and more conversational than typed ones, so they often reveal the intent behind the search more clearly. Recognising this difference is key to optimising your content.

## Optimising for Spoken Queries

To capture voice traffic, think about how people naturally speak. This often means focusing on who, what, where, when, why, and how questions.

Users might ask, “What is the best Italian restaurant near me?” or “How do I fix a leaky faucet?” Including question‑based headings and short, direct answers increases the likelihood that search engines will feature your content in voice results. Structured data, such as FAQ and How‑to schema, helps search engines understand your pages and extract concise answers.

And don’t forget local optimisation: voice queries often have local intent, so ensure your contact details, opening hours and location information are up to date.

## Voice Search Best Practices

Improve readability by keeping sentences short and clear. Use natural language rather than jargon, and anticipate follow‑up questions by linking related topics.

Since voice search results often rely on featured snippets, create content that’s easy to summarise. Bulleted lists and tables can help search engines present your information succinctly.

Keep your site mobile friendly, as many voice searches occur on smartphones. Finally, monitor your analytics to see how voice visitors behave differently from other users, and adjust your strategy accordingly.

## SEO Takeaways and Next Steps

Voice search is not a fad—it’s a growing part of how people discover information. By crafting content that aligns with conversational queries and prioritises clarity, you can tap into this expanding audience.

For guidance on voice search optimisation tailored to your business, contact me at info@phil‑carr.co.uk or 01226 697 325.

If you need help with your SEO strategy, get in touch by emailing [info@phil-carr.co.uk](mailto:info@phil-carr.co.uk) or calling 01226 697 325.

## Key Takeaways

- **The Growing Role of Voice:** Although voice search did not overtake traditional search in 2023, it continued to grow steadily as smart speakers and voice assistants became more integrated into daily life.
- **Optimising for Spoken Queries:** To capture voice traffic, think about how people naturally speak.
- **Voice Search Best Practices:** Improve readability by keeping sentences short and clear.

## Update — May 2026

Reviewed in May 2026 against the last two core update roll-outs. The bit that's aged best is the emphasis on primary content quality over surface tweaks; the bit worth re-reading is the section on recovery timelines — they've stretched to closer to two full crawl cycles.

## About the author

Phil Carr is an SEO consultant and web strategist focused on helping businesses
grow through technical optimisation, content strategy, and data-driven decision making. His
work combines hands-on experience with ongoing research into algorithm updates and emerging
search trends.

[Read Phil’s bio](/#authorModal)

## Other News Articles

[![2024 Algorithm Updates – Navigating a Year of Core Changes and Spam Crackdowns featured image](/images/blog/generated/2024-algorithm-updates-navigating-a-year-of-core-changes-and-spam-crackdowns.jpg)](/blog/2024-algorithm-updates-navigating-a-year-of-core-changes-and-spam-crackdowns)

### [2024 Algorithm Updates – Navigating a Year of Core Changes and Spam Crackdowns](/blog/2024-algorithm-updates-navigating-a-year-of-core-changes-and-spam-crackdowns)

[![2023 SEO Lessons and Forward View featured image](/images/blog/generated/2023-seo-lessons-and-forward-view.jpg)](/blog/2023-seo-lessons-and-forward-view)

### [2023 SEO Lessons and Forward View](/blog/2023-seo-lessons-and-forward-view)

[![A Practical Plan for AI-Driven Search – A Ninety Day Playbook featured image](/images/blog/generated/a-practical-plan-for-ai-driven-search-a-ninety-day-playbook.jpg)](/blog/a-practical-plan-for-ai-driven-search-a-ninety-day-playbook)

### [A Practical Plan for AI-Driven Search – A Ninety Day Playbook](/blog/a-practical-plan-for-ai-driven-search-a-ninety-day-playbook)

[![Looking Ahead: Lessons from 2024 for the Future featured image](/images/blog/generated/looking-ahead-lessons-from-2024-for-the-future.jpg)](/blog/looking-ahead-lessons-from-2024-for-the-future)

### [Looking Ahead: Lessons from 2024 for the Future](/blog/looking-ahead-lessons-from-2024-for-the-future)

We use cookies to improve your experience, analyse site traffic, and understand how you interact with
our content. Read our [cookie policy](/cookies) to learn more.

Reject optionalAccept cookies [Privacy policy](/privacy-policy)

reCAPTCHA

Recaptcha requires verification.

protected by **reCAPTCHA**`,
};
